import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface ContinuingBondsJournalProps {
  onBack: () => void;
}

export const ContinuingBondsJournal = ({ onBack }: ContinuingBondsJournalProps) => {
  const { logExercise, isLogging } = useUniversalTracking();
  const [responses, setResponses] = useState({
    connectionMoments: '',
    ongoingInfluence: '',
    conversationWithThem: '',
    ritualOrSymbol: '',
    gratitudeStatement: ''
  });

  const handleSave = async () => {
    await logExercise({
      exercise_type: 'continuing_bonds_journal',
      exercise_name: 'Continuing Bonds Journal',
      category: 'grief',
      duration_minutes: 20,
      completion_data: {
        sections_completed: Object.values(responses).filter(v => v.trim()).length
      },
      xp_earned: 25
    });
    toast.success('Your continuing bonds journal has been saved', {
      description: 'Your connection is honoured and held'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to Grief Tools
      </Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-soft-accent/10">
              <Heart className="h-6 w-6 text-soft-accent" />
            </div>
            <div>
              <CardTitle className="text-2xl font-light">Continuing Bonds Journal</CardTitle>
              <CardDescription>
                Maintain a healthy, evolving connection with what/who was lost
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Introduction */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-base font-medium">You Don't Have to "Let Go"</p>
              <p className="text-base leading-relaxed">
                Modern grief research (Klass, Silverman & Nickman) has shown that healthy grieving doesn't mean severing 
                the connection-it means transforming it. The bond continues in a new form. This journal helps you honour 
                that continuing relationship in a way that supports your healing, not your suffering.
              </p>
              <p className="text-base leading-relaxed">
                Whether you've lost a child, a partner, a parent, or anyone precious to you-your love for them endures. 
                This is a space to honour that.
              </p>
            </CardContent>
          </Card>

          {/* Prompt 1: Connection Moments */}
          <div className="space-y-2">
            <label className="text-base font-medium flex items-center gap-2">
              <span className="text-lg">✨</span>
              When do you still feel connected to them?
            </label>
            <p className="text-sm">
              Certain places, songs, activities, times of day... when does their presence feel close?
            </p>
            <Textarea
              value={responses.connectionMoments}
              onChange={(e) => setResponses({ ...responses, connectionMoments: e.target.value })}
              placeholder="When I'm in nature, when I hear their favourite song, when I cook their recipe, in dreams..."
              className="min-h-[120px] bg-background/80 leading-relaxed text-base"
            />
          </div>

          {/* Prompt 2: Ongoing Influence */}
          <div className="space-y-2">
            <label className="text-base font-medium flex items-center gap-2">
              <span className="text-lg">🌱</span>
              How are they still influencing you?
            </label>
            <p className="text-sm">
              What values, lessons, or qualities did they embody that you now carry forward?
            </p>
            <Textarea
              value={responses.ongoingInfluence}
              onChange={(e) => setResponses({ ...responses, ongoingInfluence: e.target.value })}
              placeholder="Their kindness changed how I treat strangers, their resilience inspires me, their sense of humour helps me through dark days..."
              className="min-h-[120px] bg-background/80 leading-relaxed text-base"
            />
          </div>

          {/* Prompt 3: Conversation */}
          <div className="space-y-2">
            <label className="text-base font-medium flex items-center gap-2">
              <span className="text-lg">💬</span>
              What would you want to tell them today?
            </label>
            <p className="text-sm">
              If they could hear you right now, what would you share? Updates, feelings, questions...
            </p>
            <Textarea
              value={responses.conversationWithThem}
              onChange={(e) => setResponses({ ...responses, conversationWithThem: e.target.value })}
              placeholder="I got that promotion you believed I could get. I'm struggling with... I want you to know... I wonder if you can see..."
              className="min-h-[140px] bg-background/80 leading-relaxed text-base"
            />
          </div>

          {/* Prompt 4: Ritual or Symbol */}
          <div className="space-y-2">
            <label className="text-base font-medium flex items-center gap-2">
              <span className="text-lg">🕯️</span>
              Is there a ritual or symbol that helps you feel connected?
            </label>
            <p className="text-sm">
              Lighting a candle, wearing their jewellery, visiting a place, doing an activity they loved...
            </p>
            <Textarea
              value={responses.ritualOrSymbol}
              onChange={(e) => setResponses({ ...responses, ritualOrSymbol: e.target.value })}
              placeholder="I light a candle on Sundays, I wear their ring, I visit their favourite bench, I keep their photo nearby..."
              className="min-h-[100px] bg-background/80 leading-relaxed text-base"
            />
          </div>

          {/* Prompt 5: Gratitude */}
          <div className="space-y-2">
            <label className="text-base font-medium flex items-center gap-2">
              <span className="text-lg">🙏</span>
              What are you most grateful for about having known them?
            </label>
            <p className="text-sm">
              Even amidst the pain of loss, what gift did this relationship bring to your life?
            </p>
            <Textarea
              value={responses.gratitudeStatement}
              onChange={(e) => setResponses({ ...responses, gratitudeStatement: e.target.value })}
              placeholder="I'm grateful for the way they loved me unconditionally, for the memories we made, for how they changed my life..."
              className="min-h-[120px] bg-background/80 leading-relaxed text-base"
            />
          </div>

          {/* Mirror of Meaning Reflection */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-base font-medium">Mirror of Meaning: What Does This Connection Reflect About You?</p>
              <div className="text-base leading-relaxed space-y-2">
                <p>
                  The depth of your grief reflects the depth of your love. Your ability to maintain this bond whilst also 
                  moving forward with your life shows incredible resilience and capacity for nuanced, mature love.
                </p>
                <p className="text-foreground">
                  This isn't about "moving on"-it's about bringing them with you in a new way as you continue to grow and live. 
                  They remain part of your story, woven into who you are becoming.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1">
              Save as Draft
            </Button>
            <Button className="flex-1" onClick={handleSave} disabled={isLogging}>
              <Heart className="w-4 h-4 mr-2" />
              {isLogging ? 'Saving...' : 'Save Journal Entry'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* The Science */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="space-y-3">
            <p className="text-base font-medium">The Research Behind Continuing Bonds</p>
            <div className="text-base leading-relaxed space-y-2">
              <p>
                <span className="font-medium text-foreground">Continuing Bonds Theory</span> revolutionised grief therapy. 
                For decades, the field told people to "let go" and "move on." Research now shows that's not only unhelpful-it can 
                be harmful. Most people who grieve well maintain an internal relationship with who or what was lost.
              </p>
              <p>
                <span className="font-medium text-foreground">Neuroscience perspective:</span> The neural networks in your brain 
                that represent this person don't disappear. Your brain doesn't want to erase them-it wants to reorganise the 
                relationship so you can access the good whilst managing the pain.
              </p>
              <p>
                <span className="font-medium text-foreground">Healthy vs unhealthy bonds:</span> A healthy continuing bond 
                supports your life moving forward. An unhealthy one keeps you frozen. This journal helps you maintain 
                connection whilst also honouring your own growth and healing.
              </p>
              <p className="text-foreground">
                For bereaved parents especially, research shows that maintaining an ongoing bond with your child can be deeply 
                healing and doesn't prevent you from living-it helps you carry them forward with love.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
